import { diffArrays, diffWords } from 'diff'

/**
 * Content fields shown in the user story version history, in display order
 */
export const FIELDS = [
  { key: 'title', name: 'Story statement', label: 'story statement' },
  { key: 'description', name: 'Acceptance criteria', label: 'acceptance criteria' },
  { key: 'tech_notes', name: 'Technical Notes', label: 'technical notes' },
  { key: 'test_notes', name: 'Testing Notes', label: 'testing notes' }
]

const MAX_LEVEL = 3
const BLOCK_TAGS = ['P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'BLOCKQUOTE', 'PRE', 'DIV', 'FIGURE', 'TABLE']

/**
 * Convert wysiwyg HTML into lines of { level, text }, list nesting becomes the level
 */
export function htmlToLines (html) {
  if (!html) return []

  const root = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html').body.firstChild
  const lines = []
  const push = (text, level = 0) => {
    text = text.replace(/\s+/g, ' ').trim()
    if (text) lines.push({ level: Math.min(level, MAX_LEVEL), text })
  }

  const walkList = (list, level) => {
    const items = Array.from(list.children).filter(item => item.tagName === 'LI')
    const ordered = list.tagName === 'OL'

    items.forEach((item, i) => {
      const clone = item.cloneNode(true)
      clone.querySelectorAll('ul, ol').forEach(nested => nested.remove())
      push(`${ordered ? `${i + 1}.` : '•'} ${clone.textContent}`, level)

      Array.from(item.children)
        .filter(child => child.tagName === 'UL' || child.tagName === 'OL')
        .forEach(nested => walkList(nested, level + 1))
    })
  }

  const walk = (node) => {
    Array.from(node.childNodes).forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        push(child.textContent)
      } else if (child.tagName === 'UL' || child.tagName === 'OL') {
        walkList(child, 0)
      } else if (child.tagName === 'BR') {
        // Line breaks between inline content are handled by the surrounding text nodes
      } else if (BLOCK_TAGS.includes(child.tagName) && child.querySelector('p, ul, ol, div, h1, h2, h3, h4, h5, h6')) {
        walk(child)
      } else {
        push(child.textContent)
      }
    })
  }

  walk(root)

  return lines
}

/**
 * Convert plain text into lines, one per non empty line
 */
export function textToLines (text) {
  if (!text) return []

  return String(text).split(/\r?\n/).map(line => line.trim()).filter(Boolean).map(text => ({ level: 0, text }))
}

export function fieldLines (key, value) {
  return key === 'description' ? htmlToLines(value) : textToLines(value)
}

/**
 * Normalised value used to decide if a field changed, ignoring whitespace and empty paragraphs
 */
export function normaliseField (key, value) {
  if (!value) return ''

  if (key === 'description') {
    return String(value)
      .replace(/&nbsp;|\u00a0/g, ' ')
      .replace(/<p>\s*<\/p>/g, '')
      .replace(/\s+/g, ' ')
      .replace(/>\s+</g, '><')
      .trim()
  }

  return textToLines(value).map(line => line.text).join('\n')
}

/**
 * How a field differs between two states: 'same', 'formatting' (markup only) or 'text'
 */
export function fieldChange (key, from, to) {
  if (normaliseField(key, from) === normaliseField(key, to)) return 'same'

  const fromText = fieldLines(key, from).map(line => `${line.level}:${line.text}`).join('\n')
  const toText = fieldLines(key, to).map(line => `${line.level}:${line.text}`).join('\n')

  return fromText === toText ? 'formatting' : 'text'
}

/**
 * Word level diff of a modified line, returns the left (removed) and right (added) parts
 */
export function diffLineWords (from, to) {
  const parts = diffWords(from, to)

  return {
    left: parts.filter(part => !part.added).map(part => ({ value: part.value, removed: !!part.removed })),
    right: parts.filter(part => !part.removed).map(part => ({ value: part.value, added: !!part.added }))
  }
}

/**
 * Line level diff, pairing removed and added lines in the same region as modified lines.
 * Returns ops of { type: eq|del|ins|mod, a, b, words }
 */
export function diffLines (fromLines, toLines) {
  const chunks = diffArrays(fromLines, toLines, {
    comparator: (a, b) => a.level === b.level && a.text === b.text
  })

  const ops = []
  let removed = []
  let added = []

  const flush = () => {
    const length = Math.max(removed.length, added.length)

    for (let i = 0; i < length; i++) {
      if (removed[i] && added[i]) {
        ops.push({ type: 'mod', a: removed[i], b: added[i], words: diffLineWords(removed[i].text, added[i].text) })
      } else if (removed[i]) {
        ops.push({ type: 'del', a: removed[i] })
      } else {
        ops.push({ type: 'ins', b: added[i] })
      }
    }

    removed = []
    added = []
  }

  chunks.forEach((chunk) => {
    if (chunk.removed) {
      removed = removed.concat(chunk.value)
    } else if (chunk.added) {
      added = added.concat(chunk.value)
    } else {
      flush()
      chunk.value.forEach((line, i) => ops.push({ type: 'eq', a: line, b: chunk.value[i] }))
    }
  })

  flush()

  return ops
}

/**
 * Added = added + modified lines, removed = removed + modified lines
 */
export function countOps (ops) {
  return ops.reduce((counts, op) => {
    if (op.type === 'ins' || op.type === 'mod') counts.added++
    if (op.type === 'del' || op.type === 'mod') counts.removed++

    return counts
  }, { added: 0, removed: 0 })
}

/**
 * Collapse runs of more than 3 unchanged lines down to one context line at each end
 */
export function collapseOps (ops) {
  const result = []

  for (let i = 0; i < ops.length;) {
    if (ops[i].type !== 'eq') {
      result.push(ops[i++])
      continue
    }

    let j = i
    while (j < ops.length && ops[j].type === 'eq') j++

    const run = ops.slice(i, j)

    if (run.length > 3) {
      const keepStart = i > 0
      const keepEnd = j < ops.length

      if (keepStart) result.push(run[0])
      result.push({ type: 'gap', count: run.length - (keepStart ? 1 : 0) - (keepEnd ? 1 : 0) })
      if (keepEnd) result.push(run[run.length - 1])
    } else {
      result.push(...run)
    }

    i = j
  }

  return result
}

/**
 * Diff every content field between two version states
 */
export function diffStates (fromState, toState) {
  return FIELDS.map((field) => {
    const fromLines = fieldLines(field.key, fromState[field.key])
    const toLines = fieldLines(field.key, toState[field.key])
    const ops = diffLines(fromLines, toLines)
    const changes = ops.filter(op => op.type !== 'eq').length
    const formatting = !changes && fieldChange(field.key, fromState[field.key], toState[field.key]) === 'formatting'

    return { ...field, fromLines, toLines, ops, changes, formatting, ...countOps(ops) }
  })
}

const sameContent = (a, b) => FIELDS.every(field => fieldChange(field.key, a[field.key], b[field.key]) === 'same')

const joinLabels = labels => labels.length > 1
  ? `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`
  : labels[0]

function describe (version, previous, versions) {
  if (!previous) return 'Created story'

  const changes = FIELDS.map(field => ({
    label: field.label,
    change: fieldChange(field.key, previous.state[field.key], version.state[field.key])
  }))

  const edited = changes.filter(field => field.change === 'text').map(field => field.label)
  const formatted = changes.filter(field => field.change === 'formatting').map(field => field.label)

  if (edited.length) return `Edited ${joinLabels(edited)}`
  if (formatted.length) return `Formatting changes to ${joinLabels(formatted)}`

  return 'No content changes'
}

export function buildVersions (entries) {
  const versions = entries.slice().reverse()

  versions.forEach((version, index) => {
    version.number = index + 1
    const previous = versions[index - 1]

    version.current = index === versions.length - 1
    version.description = describe(version, previous, versions)
    version.counts = previous
      ? diffStates(previous.state, version.state).reduce((total, field) => ({
        added: total.added + field.added,
        removed: total.removed + field.removed
      }), { added: 0, removed: 0 })
      : null
  })

  return versions
}

/**
 * Full plain text of a version for copying
 */
export function versionText (version) {
  return FIELDS.map((field) => {
    const lines = fieldLines(field.key, version.state[field.key])
      .map(line => `${'    '.repeat(line.level)}${line.text}`)

    return `${field.name}\n${lines.length ? lines.join('\n') : '(empty)'}`
  }).join('\n\n')
}

